import { useState, useRef, useCallback } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, ChevronRight, GripVertical, Trash2, Layers } from 'lucide-react';
import type { FormPage, FormField } from '../../../types/forms';
import type { DragData, DropData } from './SlidePanel';

interface PageContainerProps {
  page: FormPage;
  fields: FormField[];
  pageIndex: number;
  isSelected: boolean;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onSelect: () => void;
  onUpdateTitle: (title: string) => void;
  onDelete: () => void;
  children: React.ReactNode;
}

export default function PageContainer({
  page,
  fields,
  pageIndex,
  isSelected,
  isCollapsed,
  onToggleCollapse,
  onSelect,
  onUpdateTitle,
  onDelete,
  children,
}: PageContainerProps) {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState(page.title || '');
  const inputRef = useRef<HTMLInputElement>(null);

  // ── dnd-kit (mobile-ui.4.C D3) ────────────────────────
  // Header is the page drag handle + page reorder target; the whole
  // container is the drop zone for moving a field INTO this page.
  const { attributes, listeners, setNodeRef: setPageDragRef, transform, isDragging } = useDraggable({
    id: `page-${page.id}`,
    data: { kind: 'page', pageIndex } satisfies DragData,
  });
  const pageTarget = useDroppable({
    id: `page-target-${page.id}`,
    data: { kind: 'page-target', pageIndex } satisfies DropData,
  });
  const containerDrop = useDroppable({
    id: `page-container-${page.id}`,
    data: { kind: 'page-container', pageId: page.id } satisfies DropData,
  });
  const isPageDragOver = pageTarget.isOver;
  const isFieldDragOver = containerDrop.isOver;

  const displayTitle = page.title || `Página ${pageIndex + 1}`;

  const handleTitleDoubleClick = useCallback(() => {
    setEditTitle(page.title || `Página ${pageIndex + 1}`);
    setIsEditingTitle(true);
    setTimeout(() => inputRef.current?.select(), 0);
  }, [page.title, pageIndex]);

  const handleTitleCommit = useCallback(() => {
    const trimmed = editTitle.trim();
    if (trimmed && trimmed !== page.title) {
      onUpdateTitle(trimmed);
    }
    setIsEditingTitle(false);
  }, [editTitle, page.title, onUpdateTitle]);

  const handleTitleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Stop bubbling so Space/Enter while renaming never arms the KeyboardSensor
    e.stopPropagation();
    if (e.key === 'Enter') handleTitleCommit();
    if (e.key === 'Escape') setIsEditingTitle(false);
  }, [handleTitleCommit]);

  const handleDelete = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const count = fields.length;
    const msg = count > 0
      ? `Mover ${count} campo${count > 1 ? 's' : ''} para a página anterior e deletar "${displayTitle}"?`
      : `Deletar "${displayTitle}"?`;
    if (window.confirm(msg)) onDelete();
  }, [fields.length, displayTitle, onDelete]);

  return (
    <div
      ref={containerDrop.setNodeRef}
      style={transform ? { transform: CSS.Translate.toString(transform), position: 'relative', zIndex: 50 } : undefined}
      className={`rounded-lg border ${isDragging ? '' : 'transition-all'} ${
        isPageDragOver
          ? 'border-primary-400 bg-primary-500/5'
          : isFieldDragOver
            ? 'border-primary-400/50 bg-primary-500/5'
            : isSelected
              ? 'border-primary-500 bg-primary-500/5'
              : 'border-white/10 bg-white/[0.02]'
      } ${isPageDragOver ? 'ring-1 ring-primary-400/30' : ''} ${isSelected && !isPageDragOver ? 'ring-1 ring-primary-500/30' : ''}`}
    >
      {/* Header — page drag handle (reorder pages) */}
      <div
        ref={(node) => { setPageDragRef(node); pageTarget.setNodeRef(node); }}
        className="flex items-center gap-1.5 px-2 py-1.5 cursor-pointer group select-none"
        onClick={() => { if (!isDragging) { onSelect(); onToggleCollapse(); } }}
        {...attributes}
        {...listeners}
      >
        {/* Drag handle — always visible on coarse pointers (D3) */}
        <span className="flex-shrink-0" style={{ touchAction: 'none' }}>
          <GripVertical className="w-3 h-3 text-neutral-600 opacity-0 group-hover:opacity-100 pointer-coarse:opacity-100 transition-opacity cursor-grab" />
        </span>

        {/* Collapse icon */}
        {isCollapsed
          ? <ChevronRight className="w-3.5 h-3.5 text-neutral-500 flex-shrink-0" />
          : <ChevronDown className="w-3.5 h-3.5 text-neutral-500 flex-shrink-0" />
        }

        {/* Page icon */}
        <Layers className="w-3 h-3 text-primary-400/70 flex-shrink-0" />

        {/* Title (inline edit on double-click) */}
        {isEditingTitle ? (
          <input
            ref={inputRef}
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            onBlur={handleTitleCommit}
            onKeyDown={handleTitleKeyDown}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            className="flex-1 min-w-0 text-[11px] font-medium text-neutral-200 bg-transparent border-b border-primary-400 outline-none px-0.5"
            maxLength={80}
            autoFocus
          />
        ) : (
          <span
            className="flex-1 min-w-0 text-[11px] font-medium text-neutral-300 truncate"
            onDoubleClick={(e) => { e.stopPropagation(); handleTitleDoubleClick(); }}
            title="Duplo clique para editar"
          >
            {displayTitle}
          </span>
        )}

        {/* Field count badge */}
        <span className="text-[10px] text-neutral-500 tabular-nums flex-shrink-0">
          {fields.length}
        </span>

        {/* Delete button — always visible on coarse pointers (D3) */}
        <button
          onClick={handleDelete}
          onPointerDown={(e) => e.stopPropagation()}
          className="p-0.5 rounded text-neutral-600 hover:text-red-400 opacity-0 group-hover:opacity-100 pointer-coarse:opacity-100 transition-all flex-shrink-0"
          title="Deletar página"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>

      {/* Body — field thumbnails */}
      {!isCollapsed && (
        <div className="px-1.5 pb-1.5 space-y-1">
          {fields.length === 0 ? (
            <div className="text-center text-neutral-600 text-[10px] py-3 border border-dashed border-white/10 rounded-lg">
              Arraste campos aqui
            </div>
          ) : (
            children
          )}
        </div>
      )}
    </div>
  );
}
